// Widget de Aura: lo carga cualquier sistema con
//   <script src="https://<api>/widget.js" data-tenant="lupa" data-token-provider="getLauraToken" async></script>
// (o data-token-url="/api/aura/token": un endpoint del sistema que devuelve su token de sesión)
// (o data-access-key="..." sin sesión de un sistema) y se inicializa solo con los data-* del <script>
// (REQUIREMENTS 6.4). Con data-mode="page" es la página de chat de la API (`/`).
import { type ChatInfoResponse } from '@aura/shared';
import { AccessKeyAuth, ApiError, ChatApi } from './api.js';
import { readConfig } from './config.js';
import { type AccessKeyPrompt, AuraAssistant } from './element.js';
import { TokenAuth, tokenFromProvider, tokenFromUrl } from './token-auth.js';

const TAG = 'aura-assistant';
/** Donde la página de chat recuerda la clave que escribió el usuario (la misma que antes). */
const KEY_STORAGE = 'aura.accessKey';

const local = {
  get(key: string): string | undefined {
    try {
      return localStorage.getItem(key) ?? undefined;
    } catch {
      return undefined; // sin almacenamiento (modo privado): se pide en cada carga
    }
  },
  set(key: string, value: string | null): void {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* idem */
    }
  },
};

async function init(script: HTMLScriptElement | null): Promise<void> {
  if (document.querySelector(TAG)) return; // el script se incluyó dos veces
  const config = readConfig(script);
  if (!config) {
    console.warn('[aura] No se pudo saber la URL de la API: falta data-api-url.');
    return;
  }
  const page = config.mode === 'page';
  // El token de sesión del sistema: de una función de la página o de un endpoint del sistema.
  const tokenSource = config.tokenProvider
    ? tokenFromProvider(config.tokenProvider)
    : config.tokenUrl
      ? tokenFromUrl(config.tokenUrl)
      : undefined;
  if (tokenSource && !config.tenant) {
    console.warn(
      '[aura] Falta data-tenant en el <script> (el sistema en Laura, ej. data-tenant="lupa").',
    );
    return;
  }
  const keyAuth = tokenSource
    ? undefined
    : new AccessKeyAuth(config.accessKey ?? (page ? local.get(KEY_STORAGE) : undefined));
  const api = new ChatApi(config.apiUrl, keyAuth ?? new TokenAuth(tokenSource!, config.tenant));
  let info: ChatInfoResponse;
  try {
    info = await api.info();
    if (info.accessKeyRequired && !keyAuth?.hasKey && !page) {
      console.warn(
        '[aura] El asistente pide una clave: falta data-token-url, data-token-provider o data-access-key en el <script>.',
      );
      return;
    }
  } catch (err) {
    // Sin sesión en el sistema, o el asistente no está disponible para él: el widget no se muestra.
    const code = err instanceof ApiError ? err.code : String(err);
    // El navegador no deja ver por qué falló un pedido bloqueado por CORS: llega como error de red.
    const hint =
      code === 'NETWORK'
        ? ` (¿la API está levantada en ${config.apiUrl}? ¿${location.origin} está en CHAT_ALLOWED_ORIGINS o en los allowed_origins del tenant?)`
        : '';
    console.warn(`[aura] El asistente no está disponible: ${code}${hint}`);
    return;
  }

  // En la página de chat el usuario escribe la clave; en un sistema viene en el <script>.
  const accessKey: AccessKeyPrompt | undefined =
    page && keyAuth && info.accessKeyRequired && !config.accessKey
      ? {
          needed: !keyAuth.hasKey,
          submit(key) {
            keyAuth.setKey(key);
            local.set(KEY_STORAGE, key);
          },
          rejected() {
            keyAuth.setKey(undefined);
            local.set(KEY_STORAGE, null);
          },
        }
      : undefined;

  const subtitle = config.title ?? `Asistente de ${info.name}`;
  if (page) document.title = `${info.assistantName} · ${subtitle}`;
  if (!customElements.get(TAG)) customElements.define(TAG, AuraAssistant);
  const el = document.createElement(TAG) as AuraAssistant;
  document.body.append(el);
  el.setup(api, {
    assistantName: info.assistantName,
    subtitle,
    primaryColor: config.primaryColor,
    mode: config.mode,
    showSources: config.showSources,
    accessKey,
  });
}

// `currentScript` solo vale mientras el script se ejecuta: se lee antes de cualquier await.
const script = document.currentScript as HTMLScriptElement | null;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void init(script), { once: true });
} else {
  void init(script);
}
